/**
 * Detects when a newer frontend build has been deployed while this tab is open.
 *
 * Every built asset carries the build id as a filename suffix
 * (`assets/[name]-[hash]-<buildId>.js`, see vite.config.ts) and `index.html`
 * is always served with `Cache-Control: no-store`. Re-fetching `index.html`
 * and checking whether it still references assets from the currently running
 * build is therefore enough to notice a deploy — no backend endpoint needed.
 */

export const DEV_BUILD_ID = 'dev';

export const APP_BUILD_ID: string =
  typeof __APP_BUILD_ID__ === 'string' && __APP_BUILD_ID__ ? __APP_BUILD_ID__ : DEV_BUILD_ID;

const HASHED_ASSET_PATTERN = /\/assets\/[^"'\s]+\.js/;

/**
 * Returns true when `html` is a built SPA shell that no longer references the
 * assets of `currentBuildId`. Returns false (never "newer") when the document
 * cannot be recognised as a built shell — the Vite dev server, an error page,
 * or a maintenance page must not trigger the banner.
 */
export function isNewerBuildReferenced(html: string, currentBuildId: string): boolean {
  if (!currentBuildId || currentBuildId === DEV_BUILD_ID) return false;
  if (!HASHED_ASSET_PATTERN.test(html)) return false;
  return !html.includes(`-${currentBuildId}.js`);
}

export async function fetchIsNewerBuildAvailable(
  currentBuildId: string = APP_BUILD_ID,
  fetchImpl: typeof fetch = fetch
): Promise<boolean> {
  if (currentBuildId === DEV_BUILD_ID) return false;

  try {
    const response = await fetchImpl(`${import.meta.env.BASE_URL}index.html`, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) return false;
    return isNewerBuildReferenced(await response.text(), currentBuildId);
  } catch {
    // Offline or transient network failure — try again on the next tick.
    return false;
  }
}
